import React, { useEffect, useState } from 'react';
import { getAllModuleResults } from '../../api/moduleApi';
import { getSystemSettings } from '../../api/adminApi';

const Leaderboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [academicYearStartDate, setAcademicYearStartDate] = useState({ month: 9, day: 22 });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        try {
          const settings = await getSystemSettings();
          if (settings.academic_year_start_date) {
            const [m, d] = settings.academic_year_start_date.split('-').map(Number);
            setAcademicYearStartDate({ month: m, day: d });
          }
        } catch (settingsErr) {
          console.warn('Could not load academic year settings:', settingsErr);
        }

        const results = await getAllModuleResults();
        setData(results || []);
      } catch (err) {
        console.error('Failed loading leaderboard data', err);
        setError(err.message || 'Failed to load leaderboard data');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const rows = [];
  data.forEach((mod) => {
    if (!mod.surveys || mod.surveys.length === 0) {
      rows.push({
        module_code: mod.module_code || mod.module_name,
        module_name: mod.module_name,
        year: '-',
        responses: '-',
        avg: null,
        avg_numeric: null,
        module_id: mod.module_id,
        module_name_full: mod.module_name,
        response_rate: null,
        responses_completed: 0,
        total_enrolled: 0
      });
    } else {
      mod.surveys.forEach((s) => {
        const responsesCompleted = s.responses_completed || 0;
        const totalEnrolled = s.total_enrolled || 0;
        const responseRate = totalEnrolled ? (responsesCompleted / totalEnrolled) * 100 : 0;
        const pct = totalEnrolled ? `${Math.round((responsesCompleted / totalEnrolled) * 100)}% (${responsesCompleted}/${totalEnrolled})` : `${responsesCompleted} responses`;
        rows.push({
          module_code: mod.module_code || mod.module_name,
          module_name: mod.module_name,
          year: s.survey_year || '-',
          responses: pct,
          avg: s.avg_rating != null ? s.avg_rating.toFixed(2) : '-',
          avg_numeric: s.avg_rating,
          module_id: mod.module_id,
          survey_id: s.survey_id,
          response_rate: responseRate,
          responses_completed: responsesCompleted,
          total_enrolled: totalEnrolled
        });
      });
    }
  });

  const uniqueYears = Array.from(new Set(rows.map((r) => r.year).filter((y) => y !== '-'))).sort((a, b) => b - a);

  const getCurrentAcademicYearStart = () => {
    const now = new Date();
    const startMonth = Number(academicYearStartDate?.month || 9);
    const startDay = Number(academicYearStartDate?.day || 1);
    const startThisYear = new Date(now.getFullYear(), startMonth - 1, startDay);
    return now >= startThisYear ? now.getFullYear() : now.getFullYear() - 1;
  };

  useEffect(() => {
    if (selectedYear === null && uniqueYears.length > 0) {
      const currentAcademicYear = getCurrentAcademicYearStart();
      if (uniqueYears.includes(currentAcademicYear)) {
        setSelectedYear(currentAcademicYear);
        return;
      }
      const notFutureYears = uniqueYears.filter((y) => y <= currentAcademicYear);
      if (notFutureYears.length > 0) {
        setSelectedYear(notFutureYears[0]);
        return;
      }
      setSelectedYear(uniqueYears[0]);
    }
  }, [uniqueYears, selectedYear, academicYearStartDate]);

  const filteredRows = selectedYear ? rows.filter((r) => r.year === selectedYear) : rows;
  const chartRows = filteredRows
    .filter((r) => r.avg_numeric != null)
    .sort((a, b) => b.avg_numeric - a.avg_numeric);

  const getAcademicYearDisplay = (calendarYear) => {
    if (!calendarYear || calendarYear === '-') return '-';
    return `${calendarYear}-${calendarYear + 1}`;
  };

  if (loading) return <div className="text-center py-10 text-indigo-500 font-medium">Loading leaderboard...</div>;
  if (error) return <div className="p-6 bg-red-100 rounded-xl shadow-lg text-red-700 font-medium">Error: {error}</div>;

  return (
    <div className="p-6 bg-white rounded-xl shadow-lg">
      <h2 className="text-3xl font-bold mb-4 text-indigo-700">Leaderboard</h2>
      <p className="text-gray-600 mb-6">Compare modules by average score and response rate. The chart shows module average ratings on a 0–5 scale.</p>

      {uniqueYears.length > 0 && (
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="font-semibold text-gray-700">Filter by Academic Year:</label>
          <select
            value={selectedYear || ''}
            onChange={(e) => setSelectedYear(e.target.value ? Number(e.target.value) : null)}
            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          >
            <option value="">All Years</option>
            {uniqueYears.map((year) => (
              <option key={year} value={year}>{year}-{year + 1}</option>
            ))}
          </select>
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span>Score scale: 0 to 5</span>
          <span className="font-medium">Target line: 4.0</span>
        </div>

        <div className="space-y-3">
          {chartRows.length > 0 ? chartRows.map((row, index) => {
            const barWidth = Math.max(0, Math.min(100, (row.avg_numeric / 5) * 100));
            return (
              <div key={`${row.module_id}-${row.survey_id || index}`} className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <div className="font-semibold text-gray-900">{row.module_code}</div>
                    <div className="text-sm text-gray-500">{row.module_name || ''}</div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                    <span className="font-semibold text-gray-900">{row.avg_numeric.toFixed(2)}</span>
                    <span>{row.responses}</span>
                    <span>{getAcademicYearDisplay(row.year)}</span>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="relative h-5 rounded-full bg-gray-200 overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-indigo-500 rounded-full transition-all duration-300" style={{ width: `${barWidth}%` }} />
                    <div className="absolute inset-y-0 right-0 left-[80%] border-l border-dotted border-gray-500/70" />
                    <div className="absolute -top-5 left-[80%] text-xs text-gray-500" style={{ transform: 'translateX(-50%)' }}>4.0</div>
                  </div>
                  <div className="mt-2 flex justify-between text-xs text-gray-500">
                    <span>0</span>
                    <span>5</span>
                  </div>
                </div>
              </div>
            );
          }) : (
            <div className="p-6 rounded-xl border border-dashed border-gray-300 text-gray-500 text-center">No module averages available for the selected year.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
